import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import agentService from '../../services/agentService';
import PageLoader from '../shared/PageLoader';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  SmartToy,
  Person,
  TrendingUp,
  CalendarToday,
} from '@mui/icons-material';
import {
  LayoutGrid,
  List as ListIconLucide,
  Search,
  Activity,
  ShieldCheck,
  Tag,
  Play,
} from 'lucide-react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import agentStyles from '../Agents/Agents.module.scss';

const Templates = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      primary: {
        main: '#e36250 !important',
      },
      secondary: {
        main: '#8b5cf6',
      },
    },
  });

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('templatesViewMode') || 'list';
  });

  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalTemplates, setTotalTemplates] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const containerRef = useRef(null);

  const handleViewModeChange = (newView) => {
    if (newView === 'card' || newView === 'list') {
      setViewMode(newView);
      localStorage.setItem('templatesViewMode', newView);
    }
  };

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter((t) => {
      const agentName = (t.agent_name || t.agent?.name || '').toLowerCase();
      const desc = (t.template_description || t.description || '').toLowerCase();
      const userName = (t.user_name || t.user?.name || t.user?.email || '').toLowerCase();
      return agentName.includes(q) || desc.includes(q) || userName.includes(q);
    });
  }, [templates, searchQuery]);

  const loadTemplates = useCallback(async (pageNum = 1, append = false) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    try {
      const response = await agentService.getTemplates(pageNum, pageSize);
      const templatesData = response.templates || response.data || [];

      if (append) {
        setTemplates((prev) => [...prev, ...templatesData]);
      } else {
        setTemplates(templatesData);
      }

      setTotalPages(response.total_pages || 0);
      setTotalTemplates(response.total || 0);
      setHasMore(pageNum < (response.total_pages || 0));
    } catch (err) {
      setError('Failed to load templates');
      console.error('Error loading templates:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [pageSize]);

  useEffect(() => {
    loadTemplates(1, false);
  }, [loadTemplates]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let isFetching = false;

    const handleScroll = () => {
      const scrollTop = container.scrollTop;
      const scrollHeight = container.scrollHeight;
      const clientHeight = container.clientHeight;

      const isNearBottom = scrollTop + clientHeight >= scrollHeight - 300;

      if (isNearBottom && hasMore && !loadingMore && !loading && !isFetching) {
        isFetching = true;
        const nextPage = page + 1;
        setPage(nextPage);
        loadTemplates(nextPage, true).finally(() => {
          isFetching = false;
        });
      }
    };

    let scrollTimeout;
    const throttledHandleScroll = () => {
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(handleScroll, 100);
    };

    container.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', throttledHandleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [hasMore, loadingMore, loading, page, totalPages, loadTemplates]);

  const handleUseTemplate = (template) => {
    if (template.agent_id) {
      navigate(`/agent-test?id=${template.agent_id}&template=true`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch (e) {
      return dateString;
    }
  };

  const getTemplateFields = (template) => {
    const agentName = template.agent_name || template.agent?.name || 'Unknown Agent';
    const userName = template.user_name || template.user?.name || template.user?.email || 'Unknown User';
    const userEmail = template.user_email || template.user?.email || '';
    const templateDescription = template.template_description || template.description || '';
    const usageCount = template.usage_count || 0;
    const createdAt = template.created_at || template.createdAt;
    return { agentName, userName, userEmail, templateDescription, usageCount, createdAt };
  };

  if (loading) {
    return (
      <ThemeProvider theme={muiTheme}>
        <PageLoader message="Loading templates..." />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={muiTheme}>
      <Box ref={containerRef} className={agentStyles.container}>
        <div className={agentStyles.studioInner}>
          <header className={agentStyles.stickyHeader}>
            <div className={agentStyles.headerTop}>
              <div className={agentStyles.titleBlock}>
                <div className={agentStyles.titleRow}>
                  <h1 className={agentStyles.pageTitle}>Templates</h1>
                  <span className={agentStyles.pageTitleMeta}>
                    {totalTemplates} Total
                  </span>
                </div>
                <div className={agentStyles.statPills}>
                  <span className={agentStyles.statPill}>
                    <span className={agentStyles.pillDot} />
                    {totalTemplates}{' '}
                    {totalTemplates === 1 ? 'Template' : 'Templates'}
                  </span>
                  <span className={agentStyles.statPill}>
                    <Activity size={12} strokeWidth={2.5} color="var(--primary)" />
                    {filteredTemplates.length} shown
                  </span>
                  <span className={agentStyles.statPill}>
                    <ShieldCheck size={12} strokeWidth={2.5} color="#22c55e" />
                    {totalPages > 0
                      ? `${totalPages} page${totalPages !== 1 ? 's' : ''}`
                      : '—'}
                  </span>
                </div>
              </div>
              <div className={agentStyles.headerActions}>
                <div className={agentStyles.viewSwitch} role="group" aria-label="View mode">
                  <Tooltip title="Card view" arrow>
                    <button
                      type="button"
                      className={`${agentStyles.viewSwitchBtn} ${viewMode === 'card' ? agentStyles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleViewModeChange('card')}
                      aria-pressed={viewMode === 'card'}
                    >
                      <LayoutGrid size={18} strokeWidth={2} />
                    </button>
                  </Tooltip>
                  <Tooltip title="List view" arrow>
                    <button
                      type="button"
                      className={`${agentStyles.viewSwitchBtn} ${viewMode === 'list' ? agentStyles.viewSwitchBtnActive : ''}`}
                      onClick={() => handleViewModeChange('list')}
                      aria-pressed={viewMode === 'list'}
                    >
                      <ListIconLucide size={18} strokeWidth={2} />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </div>
            <div className={agentStyles.filterRow}>
              <div className={agentStyles.searchWrap}>
                <Search
                  className={agentStyles.searchIcon}
                  size={18}
                  strokeWidth={2}
                  aria-hidden
                />
                <input
                  type="search"
                  className={agentStyles.searchInput}
                  placeholder="Search templates"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  aria-label="Search templates by name, description, or author"
                />
              </div>
            </div>
          </header>

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box className={`${agentStyles.content} ${agentStyles.contentSection}`}>
            {templates.length === 0 ? (
              <Box className={agentStyles.emptyState}>
                <SmartToy className={agentStyles.emptyIcon} />
                <Typography variant="h6" className={agentStyles.emptyTitle}>
                  No templates available
                </Typography>
                <Typography variant="body2" className={agentStyles.emptyDescription}>
                  Templates will appear here once agents are published as templates
                </Typography>
              </Box>
            ) : (
              <>
                {filteredTemplates.length === 0 ? (
                  <Box className={agentStyles.emptyFilterState}>
                    <Search className={agentStyles.emptyFilterIcon} size={48} strokeWidth={1.5} />
                    <Typography variant="h6" className={agentStyles.emptyFilterTitle}>
                      No templates match your search
                    </Typography>
                    <Typography variant="body2" className={agentStyles.emptyFilterDesc}>
                      Try different keywords or clear the search to see all loaded templates.
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setSearchQuery('')}
                      sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                    >
                      Clear search
                    </Button>
                  </Box>
                ) : (
                  <>
                    {viewMode === 'card' && (
                      <Box className={agentStyles.agentsContainer}>
                        {filteredTemplates.map((template) => {
                          const {
                            agentName,
                            userName,
                            userEmail,
                            templateDescription,
                            usageCount,
                            createdAt,
                          } = getTemplateFields(template);

                          return (
                            <Box
                              className={agentStyles.agentCard}
                              key={template.id || template.template_id}
                            >
                              <Box className={agentStyles.cardContent}>
                                <Box className={agentStyles.cardTopBar}>
                                  <div className={agentStyles.cardIconWrap}>
                                    <SmartToy sx={{ fontSize: 18 }} />
                                  </div>
                                </Box>
                                <Box
                                  className={agentStyles.typeBadge}
                                  sx={{ mb: 1 }}
                                >
                                  <Tag size={10} strokeWidth={2.5} />
                                  Template
                                </Box>
                                <Typography
                                  component="h3"
                                  variant="h6"
                                  className={agentStyles.cardName}
                                >
                                  {agentName}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                  <Person sx={{ fontSize: 16, color: 'text.secondary' }} />
                                  <Typography variant="caption" color="text.secondary">
                                    {userName}
                                    {userEmail && userName !== userEmail && (
                                      <span> ({userEmail})</span>
                                    )}
                                  </Typography>
                                </Box>
                                <Tooltip title={templateDescription || 'No description'} arrow>
                                  <Typography
                                    variant="body2"
                                    className={agentStyles.cardDesc}
                                    sx={{
                                      display: '-webkit-box',
                                      WebkitLineClamp: 3,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      minHeight: 48,
                                    }}
                                  >
                                    {templateDescription || ''}
                                  </Typography>
                                </Tooltip>
                                <Box
                                  className={agentStyles.featureTags}
                                  sx={{ mb: 1, flexWrap: 'wrap' }}
                                >
                                  <Box
                                    className={agentStyles.infoItem}
                                    sx={{
                                      backgroundColor: isDarkMode ? '#4caf5020' : '#4caf5010',
                                      borderColor: isDarkMode ? '#4caf5040' : '#4caf5030',
                                    }}
                                  >
                                    <TrendingUp className={agentStyles.infoIcon} sx={{ color: '#4caf50', fontSize: 16 }} />
                                    <Typography variant="caption" className={agentStyles.infoText} sx={{ color: '#4caf50' }}>
                                      {usageCount} uses
                                    </Typography>
                                  </Box>
                                  {createdAt && (
                                    <Box
                                      className={agentStyles.infoItem}
                                      sx={{
                                        backgroundColor: isDarkMode ? '#2196f320' : '#2196f310',
                                        borderColor: isDarkMode ? '#2196f340' : '#2196f330',
                                      }}
                                    >
                                      <CalendarToday className={agentStyles.infoIcon} sx={{ color: '#2196f3', fontSize: 16 }} />
                                      <Typography variant="caption" className={agentStyles.infoText} sx={{ color: '#2196f3' }}>
                                        {formatDate(createdAt)}
                                      </Typography>
                                    </Box>
                                  )}
                                </Box>
                                <div className={agentStyles.cardActions}>
                                  <button
                                    type="button"
                                    className={agentStyles.cardActionPrimary}
                                    style={{ flex: 1 }}
                                    onClick={() => handleUseTemplate(template)}
                                  >
                                    <Play size={12} fill="currentColor" />
                                    Use template
                                  </button>
                                </div>
                              </Box>
                            </Box>
                          );
                        })}
                      </Box>
                    )}

                    {viewMode === 'list' && (
                      <div className={agentStyles.agentsTableWrap}>
                        <table className={agentStyles.agentsTable}>
                          <thead>
                            <tr>
                              <th>Template</th>
                              <th>Author</th>
                              <th>Usage</th>
                              <th>Created</th>
                              <th style={{ textAlign: 'right' }}></th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredTemplates.map((template) => {
                              const {
                                agentName,
                                userName,
                                templateDescription,
                                usageCount,
                                createdAt,
                              } = getTemplateFields(template);

                              return (
                                <tr key={template.id || template.template_id}>
                                  <td>
                                    <div className={agentStyles.tableCellRow}>
                                      <div className={agentStyles.cardIconWrap}>
                                        <SmartToy sx={{ fontSize: 18 }} />
                                      </div>
                                      <div>
                                        <div className={agentStyles.tableAgentName}>{agentName}</div>
                                        <div className={agentStyles.tableAgentDesc}>
                                          {templateDescription || 'No description provided'}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                  <td>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {userName}
                                    </Typography>
                                  </td>
                                  <td>
                                    <Typography variant="body2" sx={{ fontWeight: 800, color: 'var(--text-primary)' }}>
                                      {usageCount} uses
                                    </Typography>
                                  </td>
                                  <td>
                                    <Typography variant="caption" color="text.secondary">
                                      {createdAt ? formatDate(createdAt) : '—'}
                                    </Typography>
                                  </td>
                                  <td style={{ textAlign: 'right' }}>
                                    <div className={agentStyles.tableRowActions}>
                                      <Tooltip title="Use template" arrow>
                                        <button
                                          type="button"
                                          className={`${agentStyles.tableIconBtn} ${agentStyles.tableIconBtnPrimary}`}
                                          onClick={() => handleUseTemplate(template)}
                                        >
                                          <Play size={14} fill="currentColor" />
                                        </button>
                                      </Tooltip>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {loadingMore && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mt: 4,
                          mb: 2,
                          gap: 2,
                        }}
                      >
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          Loading more templates...
                        </Typography>
                      </Box>
                    )}

                    {!hasMore && templates.length > 0 && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mt: 4,
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          Showing all {templates.length} of {totalTemplates} templates
                        </Typography>
                      </Box>
                    )}
                  </>
                )}
              </>
            )}
          </Box>
        </div>
      </Box>
    </ThemeProvider>
  );
};

export default Templates;
